import { Logger, LoggerFactory } from '../../core/logger.service';
import { Injectable } from '@angular/core';
import {
  BrandVariablesUsage,
  CampaignTemplate,
  CreateOrUpdateCampaignTemplate,
  TemplateTag,
} from 'app/campaign/templates/template.model';
import { UserLanguage } from 'app/account-settings/models/user.model';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { HttpRequestDecorator } from 'app/core/triggerbee/http.request.decorator';
import { TriggerbeeApi } from 'app/core/triggerbee/triggerbee-api.enum';
import CampaignHelpers from '../campaign-helpers';
import { PatchRequest, WidgetPreviewImage, WidgetPreviewImageType } from '../widgets.model';
import { CampaignStatus } from '../campaign.model';

@Injectable()
export class CampaignTemplatesService {
  private readonly logger: Logger;
  public allPublishedTemplatesBS = new BehaviorSubject<CampaignTemplate[]>(undefined);

  constructor(
    loggerFactory: LoggerFactory,
    private http: HttpRequestDecorator,
  ) {
    this.logger = loggerFactory.get('CampaignTemplatesService');
  }

  private getTemplateResource(
    subResource?: string,
    api: TriggerbeeApi = TriggerbeeApi.Gateway,
    isAdminRoute: boolean = false,
  ): string {
    return (
      this.http.getBaseUrl(api) + `${isAdminRoute ? '/admin' : ''}/onsite-campaign-templates` + (subResource || '')
    );
  }

  public getTemplates(request?: {
    status?: CampaignStatus;
    tags?: string[];
    take?: number;
    isAdminRoute?: boolean;
  }): Observable<Array<CampaignTemplate>> {
    let url = this.getTemplateResource(undefined, TriggerbeeApi.Gateway, request?.isAdminRoute);
    const queryParameters = [];
    if (request !== undefined) {
      if (request.tags) {
        request.tags.forEach((tag) => {
          queryParameters.push(`tags=${tag}`);
        });
      }
      if (request.status !== undefined) {
        queryParameters.push(`status=${request.status}`);
      }
      if (request.take !== undefined) {
        queryParameters.push(`take=${request.take}`);
      }
      url += '?' + queryParameters.join('&');
    }
    return this.http.get(url);
  }

  public publishTemplate(templateId: number): Observable<any> {
    const url = this.getTemplateResource(`/${templateId}/publish`, undefined, true);
    return this.http.post(url, {});
  }

  public unpublishTemplate(templateId: number): Observable<any> {
    const url = this.getTemplateResource(`/${templateId}/unpublish`, undefined, true);
    return this.http.post(url, {});
  }

  public deleteTemplate(templateId: number): Observable<any> {
    const url = this.getTemplateResource(`/${templateId}`, undefined, true);
    return this.http.delete(url);
  }

  public getTemplateById(templateId: number): Observable<CampaignTemplate> {
    const url = this.getTemplateResource(`/${templateId}`);
    return this.http.get(url);
  }

  public duplicateTemplate(templateId): Observable<CampaignTemplate> {
    const url = this.getTemplateResource(`/${templateId}/duplicate`, undefined, true);
    return this.http.post(url, { id: templateId });
  }

  public getTemplatesTags(): Observable<Array<TemplateTag>> {
    const url = this.getTemplateResource(`/tags`, TriggerbeeApi.Gateway);
    return this.http.get(url);
  }

  public getBrandVariablesUsage(): Observable<Array<BrandVariablesUsage>> {
    const url = this.getTemplateResource(`/brand-variables-usage`, TriggerbeeApi.Gateway, true);
    return this.http.get(url);
  }

  public createTemplatesTag(tag): Observable<TemplateTag> {
    const url = this.getTemplateResource(`/tags`, TriggerbeeApi.Gateway, true);
    return this.http.post(url, tag);
  }

  public updateTemplatesTag(tag: TemplateTag): Observable<TemplateTag> {
    tag = CampaignHelpers.emptyAllBackendTypeProperties(tag) as TemplateTag;
    const url = this.getTemplateResource(`/tags/${tag.id}`, TriggerbeeApi.Gateway, true);
    return this.http.put(url, tag);
  }

  public deleteTemplatesTag(tag): Observable<TemplateTag> {
    const url = this.getTemplateResource(`/tags/${tag.id}`, TriggerbeeApi.Gateway, true);
    return this.http.delete(url);
  }

  public addTagToTemplate(templateId: number, tagId: number): Observable<CampaignTemplate> {
    return this.http.post(this.getTemplateResource(`/${templateId}/tags`, undefined, true), {
      tagId: tagId,
    });
  }

  public patchTemplate<TValue>(id: number, propertyPath: string, value: TValue): Observable<CampaignTemplate> {
    const url = this.getTemplateResource(`/${id}`, undefined, true);
    return this.http.patch(url, [
      <PatchRequest>{
        op: 'replace',
        path: propertyPath,
        value: value,
      },
    ]);
  }

  public removeTagFromTemplate(templateId: number, tagId: number): Observable<CampaignTemplate> {
    return this.http.delete(this.getTemplateResource(`/${templateId}/tags/${tagId}`, undefined, true));
  }

  public createTemplate(data: CreateOrUpdateCampaignTemplate): Observable<CampaignTemplate> {
    return this.http.post(this.getTemplateResource(undefined, undefined, true), data);
  }

  public getTemplateImages(templateId: number): Observable<Array<WidgetPreviewImage>> {
    return this.http.get(this.getTemplateResource(`/${templateId}/preview-images`, TriggerbeeApi.Gateway, true));
  }

  public updateTemplateImage(
    templateId: number,
    imageId: number,
    image: WidgetPreviewImage,
  ): Observable<WidgetPreviewImage> {
    image = CampaignHelpers.emptyAllBackendTypeProperties(image) as WidgetPreviewImage;
    return this.http
      .put(this.getTemplateResource(`/${templateId}/preview-images/${imageId}`, TriggerbeeApi.Gateway, true), image)
      .pipe(tap(() => true));
  }

  public loadTemplateImage(formData: FormData): Observable<{ uri: string }> {
    const url = this.getTemplateResource(`/images`, TriggerbeeApi.Gateway, true);
    return this.http.upload(url, formData);
  }

  public addTemplateImage(
    templateId: number,
    data: {
      url: string;
      type: WidgetPreviewImageType;
    },
  ): Observable<WidgetPreviewImage> {
    const url = this.getTemplateResource(`/${templateId}/preview-images`, TriggerbeeApi.Gateway, true);
    return this.http.post(url, data);
  }

  public removeTemplateImage(templateId: number, imageId: number): Observable<void> {
    const url = this.getTemplateResource(`/${templateId}/preview-images/${imageId}`, TriggerbeeApi.Gateway, true);
    return this.http.delete(url);
  }

  public reorderingTemplate(order: number, templateId: number): Observable<CampaignTemplate> {
    const url = this.getTemplateResource(`/${templateId}/move`, TriggerbeeApi.Gateway, true);
    return this.http.put(url, { order });
  }

  public static getTemplateName(template: CampaignTemplate, userLanguage: UserLanguage): string {
    let name = template.name;
    if (userLanguage == UserLanguage.Swedish && template.nameSwedish) {
      name = template.nameSwedish;
    }
    return name;
  }
}
